import './styles.scss'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { Col, Row } from 'antd'

import B2becPagination from '@/components/B2becPagination/B2becPagination'
import B2becSorting from '@/components/B2BecSorting'
import { getSortOption } from '@/components/B2BecSorting/sortOptions'
import CustomButton from '@/components/CustomButton'
import { ChevronLeftIcon, ListViewIcon, TileViewIcon } from '@/components/Icons'
import NotFound from '@/components/NotFound'
import B2becCardSkeleton from '@/components/Skeletons/B2becCardSkeleton'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import {
  selectProductOverviewData,
  selectSubCategoryOverviewData,
} from '@/store/selectors/categorySelector'
import { trackCategory } from '@/store/slices/adobeAnalyticSlice'
import {
  getProductsByClassType,
  resetProductOverviewData,
  resetProductSubCategoryOverviewData,
} from '@/store/slices/categorySlice'
import { useDeviceDetect } from '@/hooks'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import useBreadcrumbs from '@/hooks/useBreadcrumbs'
import useCalculateScrollbarWidth from '@/hooks/useCalculateScrollbarWidth'
import useDocumentTitle from '@/hooks/useDocumentTitle'
import { ACTION_STATUS } from '@/libs/constants'
import { PAGE_TYPES, TRACK_DATA } from '@/libs/constants/adobeAnalytics'
import { getCategories } from '@/libs/utils/adobeAnalysis'
import { isValidArray } from '@/libs/utils/array'
import { linkGenerator } from '@/libs/utils/language'

import DescriptionBox from './components/DescriptionBox'
import FilterBar from './components/FilterBar'
import ProductListContainer from './components/ProductListContainer'

interface SubCategoryProps {
  title: string
  breadcrumbs: {
    title: string
    path: string
  }[]
  pageId: string
  categoryId?: string
  subCategoryId?: string
  classTypeId?: string
}

const SubCategory = (props: SubCategoryProps) => {
  const { title, breadcrumbs, pageId, categoryId, subCategoryId, classTypeId } = props
  const { setTitle, setIsTitleTranslated } = useDocumentTitle(title)
  const [setBreadcrumbsData] = useBreadcrumbs(breadcrumbs)
  const { isIpadLandscape } = useDeviceDetect()

  const { setPageInfoData } = useAdobeAnalysis(TRACK_DATA.CATEGORIES)

  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const [currentPage, setCurrentPage] = useState(1)
  const [showListView, setShowListView] = useState(false)
  const [sortOption, setSortOption] = useState(() =>
    categoryId === subCategoryId
      ? getSortOption('name.desc')
      : getSortOption('class.desc')
  )

  const subCategoryOverviewData = useAppSelector(selectSubCategoryOverviewData)
  const productOverviewData = useAppSelector(selectProductOverviewData)

  const { categoryName: parentCategoryName } = subCategoryOverviewData
  const {
    classTypes,
    productCount,
    products,
    isLoading,
    pagination,
    actionStatus,
    text,
    categoryName,
    categoryPath,
  } = productOverviewData

  const { total } = pagination

  const productTypeLinkPrefix = useMemo(() => {
    const result = /^\/accessories|\/detergents/.exec(location.pathname)
    return result && result.length > 0 ? result[0] : ''
  }, [location.pathname])

  const allClass = useMemo(
    () => ({
      categoryId: +subCategoryId,
      categoryName: t('configurationManagement.filter.all'),
      productCount,
    }),
    [subCategoryId, t, productCount],
  )

  const filteredClassTypes = useMemo(() => {
    const filterClassTypes = classTypes.filter(
      (classType) => classType?.categoryName && classType?.productCount >= 0,
    )

    if (!isIpadLandscape) {
      // In desktop view, return class, insert type 'all' also
      // In mobile view, return only classes
      filterClassTypes.unshift(allClass)
    }

    return filterClassTypes
  }, [classTypes, allClass, isIpadLandscape])

  const handleFilterByClassType = useCallback(
    (classTypeID) => {
      setCurrentPage(1)
      if (+subCategoryId === classTypeID) {
        setSortOption(getSortOption('class.desc'))
        navigate(
          linkGenerator(
            `${productTypeLinkPrefix}/category/${categoryId}/subcategory/${subCategoryId}`,
          ),
        )
      } else {
        navigate(
          linkGenerator(
            `${productTypeLinkPrefix}/category/${categoryId}/subcategory/${subCategoryId}/class/${classTypeID}`,
          ),
        )
      }
    },
    [categoryId, navigate, productTypeLinkPrefix, subCategoryId],
  )

  const handlePageChange = useCallback((nextPage) => {
    window.scrollTo(0, 0)
    setCurrentPage(nextPage)
  }, [])

  const handleSort = useCallback((order) => {
    setSortOption(order)
  }, [])

  const handleBackToCategory = useCallback(async () => {
    if (categoryId === subCategoryId) {
      navigate(linkGenerator(`/categories${productTypeLinkPrefix}`))
    } else {
      navigate(linkGenerator(`${productTypeLinkPrefix}/category/${categoryId}`))
    }
  }, [navigate, productTypeLinkPrefix, categoryId, subCategoryId])

  useEffect(() => {
    let breadcrumbsData = []
    if (parentCategoryName && categoryName) {
      breadcrumbsData.push({
        titleData: [categoryName],
        pathData: [subCategoryId],
      })
      if (parentCategoryName === categoryName) {
        breadcrumbsData.unshift({})
      } else {
        breadcrumbsData.unshift({
          titleData: [parentCategoryName],
          pathData: [categoryId],
        })
      }
    } else if (parentCategoryName) {
      breadcrumbsData = [
        {
          titleData: [parentCategoryName],
          pathData: [categoryId],
        },
      ]
    } else if (categoryName) {
      breadcrumbsData = [
        {},
        {
          titleData: [categoryName],
          pathData: [subCategoryId],
        },
      ]
    }
    const type = productTypeLinkPrefix.replace('/', '')
    if (type !== '') {
      breadcrumbsData.unshift({
        titleData: [t(`breadcrumbTitle.${type}`)],
        pathData: [type],
      })
      if (breadcrumbsData[1]) {
        breadcrumbsData[1].pathData = [type, categoryId]
      }
      if (breadcrumbsData[2]) {
        breadcrumbsData[2].pathData = [type, categoryId, subCategoryId]
      }
    }
    setBreadcrumbsData(breadcrumbsData)
    setIsTitleTranslated(true)
    setTitle(categoryName)
  }, [
    t,
    setTitle,
    setIsTitleTranslated,
    productTypeLinkPrefix,
    setBreadcrumbsData,
    parentCategoryName,
    categoryName,
    categoryId,
    subCategoryId,
  ])

  useEffect(() => {
    if (categoryName) {
      let categoryType = 'all'
      if (
        isValidArray(classTypes)
        && classTypes.some((category) => category?.categoryName === categoryName)
      ) {
        categoryType = categoryName
      }

      setPageInfoData({
        pageId,
        pageName: `sub categories:${categoryType}`,
        pageType: PAGE_TYPES.SUB_CATEGORIES,
      })
    }
  }, [setPageInfoData, categoryName, classTypes, pageId])

  useEffect(() => {
    if (!isLoading && actionStatus !== ACTION_STATUS.DEFAULT) {
      dispatch(trackCategory(getCategories(categoryPath, categoryId)))
    }
  }, [dispatch, isLoading, actionStatus, categoryPath, categoryId])

  useEffect(() => {
    dispatch(
      getProductsByClassType({
        categoryId: classTypeId ?? subCategoryId,
        pageNumber: currentPage,
        pageSize: 12,
        sortOrder: sortOption.sortOrder,
        onlyProduct: !!classTypeId,
      }),
    )
  }, [dispatch, subCategoryId, sortOption, classTypeId, currentPage])

  useEffect(
    () => () => {
      dispatch(resetProductSubCategoryOverviewData())
      dispatch(resetProductOverviewData())
    },
    [dispatch],
  )

  useCalculateScrollbarWidth()

  return actionStatus === ACTION_STATUS.FAILED ? <NotFound /> : (
    <div className="sub-category">
      <div className="sub-category__header">
        <DescriptionBox
          categoryName={categoryName}
          isLoading={isLoading}
          text={text}
        />
      </div>
      <div className="full-width-page-component__wrapper">
        <div className="sub-category-back-to-category__wrapper">
          <Row
            className="sub-category-back-to-category__content"
            justify="space-between"
            style={{ marginBottom: filteredClassTypes?.length === 0 && '0' }}
          >
            <Col hidden={isIpadLandscape}>
              <CustomButton
                buttonSize="small"
                className="back-btn"
                onClick={handleBackToCategory}
              >
                <ChevronLeftIcon />
                {t('subCategory.button.backToCategory')}
              </CustomButton>
            </Col>
            <Col>
              <B2becSorting sortOption={sortOption} onSort={handleSort} />
              <span className="change-view-icons ml-2" hidden={isIpadLandscape}>
                <TileViewIcon onClick={() => setShowListView(false)} />
                <ListViewIcon onClick={() => setShowListView(true)} />
              </span>
            </Col>
          </Row>

          <FilterBar
            allClass={allClass}
            classTypes={filteredClassTypes}
            hasClassTypeId={classTypeId}
            onFilter={handleFilterByClassType}
          />
        </div>
      </div>

      {isLoading && !showListView ? <B2becCardSkeleton number={8} /> : (
        <ProductListContainer
          dataSrc={products}
          isListView={showListView}
          isLoading={isLoading}
        />
      )}

      <B2becPagination
        current={currentPage}
        total={total}
        onPageChange={handlePageChange}
      />
    </div>
  )
}

export default SubCategory
